import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { type TSpaceBase } from '@onesource/schemas';
import {
  getMediaUrl,
  getSession,
  useGetAllProfileQuery,
  useGetPostsInfinite,
  useGetSpaceTermsAcceptance,
  type TFeedFilters,
} from '@onesource/services';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import DOMPurify from 'dompurify';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CiImageOn } from 'react-icons/ci';
import { EFeatureFlagEnum, isFeatureFlagEnabled } from 'src/hooks/use-feature-flag-utils';
import { userCanManageSpace } from 'src/utils/app-access';

import WhosOnline from '#/components/whos-online';

import { CommunityQuickFilters } from './community-quick-filters';
import { BannerStatsBar, CommunityStatsWidget, TopContributorsWidget, TrendingTagsWidget } from './community-widgets';
import { FeedFilterBar, hasActiveFeedFilters, type TFeedSearchParams } from './feed-filter-bar';
import { FeedPostsSkeleton } from './feed-posts-skeleton';
import { FeedTermsAcceptanceDialog } from './feed-terms-acceptance-dialog';
import { PostCard } from './post-card';
import { PostComposer } from './post-composer';
import { ScheduledPostsSection } from './scheduled-posts-section';

interface FeedTabContentProps {
  subSpace: TSpaceBase;
  parentSpace: TSpaceBase;
}

// Memoized wrappers for the sidebar / banner widgets: they each own a cached
// query, so a feed re-render (typing, search, data refresh) shouldn't re-run
// them unless their own props change. Declared at module scope so the memo
// identity is stable across renders.
const MemoWhosOnline = memo(WhosOnline);
const MemoTrendingTagsWidget = memo(TrendingTagsWidget);
const MemoCommunityStatsWidget = memo(CommunityStatsWidget);
const MemoTopContributorsWidget = memo(TopContributorsWidget);
const MemoBannerStatsBar = memo(BannerStatsBar);
// The filter bar hosts the (locally-stateful) search input; memoize it so a feed
// re-render for other reasons (composer toggle, scroll offset) leaves it alone.
const MemoFeedFilterBar = memo(FeedFilterBar);

// FeedTabContent renders only under this route (directly and via
// CommunityTabContent), so its search params are safe to read here.
const routeApi = getRouteApi('/_protected/community-hub/spaces/$id/community');

/**
 * Feed Tab Content
 * Displays posts from the feed sub-space
 */
export const FeedTabContent = ({ subSpace, parentSpace }: FeedTabContentProps) => {
  const searchParams = routeApi.useSearch();
  const navigate = useNavigate({ from: '/community-hub/spaces/$id/community' });
  // Gates the Trending Tags / Community Stats / Top Contributors cards (and their
  // mobile drawer tabs).
  const showCommunityWidgets = isFeatureFlagEnabled(EFeatureFlagEnum.SHOW_COMMUNITY_WIDGETS);

  const feedFilters: TFeedFilters = useMemo(
    () => ({
      search: searchParams.q,
      postTypes: searchParams.types,
      dateFrom: searchParams.from,
      dateTo: searchParams.to,
      authorId: searchParams.by,
      tagIds: searchParams.tags,
      saved: searchParams.saved,
      mine: searchParams.mine,
      sort: searchParams.sort,
    }),
    [searchParams],
  );

  const handleFiltersChange = useCallback(
    (patch: Partial<TFeedSearchParams>) => {
      navigate({ search: prev => ({ ...prev, ...patch }), replace: true });
    },
    [navigate],
  );

  // Trending-tag click toggles that tag in the feed's tag filter.
  const handleTrendingTagClick = useCallback(
    (value: string) => {
      const current = searchParams.tags ?? [];
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      handleFiltersChange({ tags: next.length > 0 ? next : undefined });
    },
    [searchParams.tags, handleFiltersChange],
  );

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetPostsInfinite(
    subSpace.id,
    undefined,
    feedFilters,
  );
  // Composer open state — the PostComposer owns all form state; the feed only
  // tracks whether it's expanded (md+) / the sheet is open (phone).
  const [composerOpen, setComposerOpen] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  // The community feed can be reached via its container (parentSpace = the container)
  // or opened directly as a feed sub-space (parentSpace = the feed itself). Question
  // tags, mentions and post links are all scoped to the top-level container, so
  // resolve it here — `parentId` is null when parentSpace already is the container.
  const containerSpaceId = parentSpace.parentId ?? parentSpace.id;
  const theme = useTheme();
  const token = getSession();
  const { data: profile } = useGetAllProfileQuery({ token });
  const { data: termsAcceptance } = useGetSpaceTermsAcceptance(subSpace.id);
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Space managers get the admin composer controls (scheduling, polls, admin
  // post types). Gated on the space.manage permission for THIS space's app —
  // the API enforces the same on create.
  const canManageSpace = userCanManageSpace(profile?.access, parentSpace);

  const showTermsDialog = !!(termsAcceptance?.requiresAcceptance && !termsAcceptance.accepted && termsAcceptance.terms);

  // Flatten posts from all pages
  const posts = useMemo(() => data?.pages.flatMap(page => page.posts) ?? [], [data?.pages]);

  const bannerSrc = isBelowMd ? subSpace.mobileBannerImage || subSpace.bannerImage : subSpace.bannerImage;
  const bannerUrl = bannerSrc ? getMediaUrl(bannerSrc) : null;

  // Virtualized posts list. The scroll context differs by breakpoint — the
  // feed column is its own scroll container from md up, while phones scroll
  // the window — so two virtualizers are set up and `enabled` picks one.
  const feedColumnRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // The list sits below the breadcrumbs/banner/composer/etc.; the virtualizer
  // needs that distance (scrollMargin) to map scroll offsets to item indexes.
  const aboveListARef = useRef<HTMLDivElement>(null);
  const aboveListBRef = useRef<HTMLDivElement>(null);
  const [listOffset, setListOffset] = useState(0);
  const hasPosts = posts.length > 0;

  useLayoutEffect(() => {
    const measure = () => {
      const listEl = listRef.current;
      if (!listEl) {
        return;
      }
      if (isBelowMd) {
        setListOffset(listEl.getBoundingClientRect().top + window.scrollY);
      } else {
        const scrollEl = feedColumnRef.current;
        if (!scrollEl) {
          return;
        }
        setListOffset(listEl.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top + scrollEl.scrollTop);
      }
    };
    measure();
    // Content above the list changes height (banner load, composer expanding,
    // scheduled posts section) — keep the margin fresh. The filter bar between
    // the wrappers only shifts by a few dozen px when stuck; overscan absorbs it.
    const observer = new ResizeObserver(measure);
    if (aboveListARef.current) {
      observer.observe(aboveListARef.current);
    }
    if (aboveListBRef.current) {
      observer.observe(aboveListBRef.current);
    }
    return () => observer.disconnect();
  }, [isBelowMd, isLoading, hasPosts]);

  // Track measured sizes by stable post id (not index) so reordering — e.g.
  // pinning a mid-feed post to the top — remaps each item's cached height to
  // its new position instead of leaving a gap where the post used to be.
  const getItemKey = useCallback((index: number) => posts[index]?.id ?? index, [posts]);

  const columnVirtualizer = useVirtualizer({
    count: posts.length,
    getScrollElement: () => feedColumnRef.current,
    estimateSize: () => 420,
    overscan: 5,
    scrollMargin: listOffset,
    enabled: !isBelowMd,
    getItemKey,
  });
  const windowVirtualizer = useWindowVirtualizer({
    count: posts.length,
    estimateSize: () => 420,
    overscan: 5,
    scrollMargin: listOffset,
    enabled: isBelowMd,
    getItemKey,
  });
  const virtualizer = isBelowMd ? windowVirtualizer : columnVirtualizer;

  // Intersection observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver]);

  if (error) {
    return <Alert severity="error">Failed to load posts: {error.message}</Alert>;
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box
          ref={feedColumnRef}
          sx={{
            flex: 1,
            // md+: the column is its own scroll container (sidebar widgets stay
            // put). xs: scrolling happens on the window — `overflow: auto` here
            // would hijack the filter bar's sticky containment without ever
            // scrolling itself, so keep it visible.
            height: { xs: 'auto', md: 'calc(100vh - 130px)' },
            overflowY: { xs: 'visible', md: 'auto' },
            '&::-webkit-scrollbar': { display: 'none' },
            maxWidth: { xs: '100%', md: 'calc(100% - 250px)' },
          }}
        >
          {/* Wrapper A — resize-observed so the virtualizer's scrollMargin
              tracks content-height changes above the list. The filter bar
              stays OUTSIDE any wrapper: a parent box would become its sticky
              containing block and end its stickiness early. */}
          <Box ref={aboveListARef}>
            {/* Breadcrumbs */}
            <Breadcrumbs
              sx={{
                mb: 2,
                fontFamily: '"Figtree", sans-serif',
                fontSize: 16,
                lineHeight: '22px',
                fontWeight: 400,
                color: theme => theme.palette.grey[600],
                '& .MuiBreadcrumbs-separator': {
                  mx: 0.6,
                },
              }}
            >
              <Box component="span" style={{ color: theme.palette.grey[600] }}>
                Community
              </Box>
              <Typography component="span" sx={{ fontSize: 16, fontWeight: 700 }}>
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(subSpace.name ?? '') }} />
              </Typography>
            </Breadcrumbs>

            {/* Banner — relative so the mobile stats bar can pin across its
                bottom edge, with the image showing (blurred) behind it. */}
            <Box sx={{ position: 'relative' }}>
              {bannerUrl ? (
                <Box
                  sx={{
                    px: { xs: 2, md: 3 },
                    pt: { xs: 2, md: 5 },
                    // Reserve room for the frosted stats bar on mobile so the
                    // banner text never runs underneath it. minHeight (not a
                    // fixed height) lets the banner grow to show the full text.
                    pb: { xs: '46px', md: 5 },
                    width: '100%',
                    minHeight: { xs: 106, md: 146 },
                    borderRadius: '11px',
                    objectFit: 'cover',
                    backgroundImage: `url(${bannerUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  {' '}
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: { xs: '20px', md: '30px' },
                      lineHeight: { xs: '20px', md: '32px' },
                      fontWeight: 700,
                      color: theme.palette.common.white,
                      '& p': {
                        margin: '0px',
                        '& span': { color: `${theme.palette.common.white} !important` },
                      },
                    }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(subSpace.description ?? '') }} />
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: 279,
                    borderRadius: '15px',
                    overflow: 'hidden',
                    bgcolor: '#f6ebe9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CiImageOn size={64} color="#d8aaa2" />
                </Box>
              )}

              {/* Mobile-only: member count + online, folded into the banner in
                  place of the desktop sidebar's stats / who's online cards; the
                  "Details" button opens the full widgets in a tabbed sheet. */}
              {isBelowMd && (
                <MemoBannerStatsBar
                  spaceId={subSpace.id}
                  selectedTagValues={searchParams.tags}
                  onTagClick={handleTrendingTagClick}
                />
              )}
            </Box>

            <Typography
              variant="h5"
              sx={{
                my: { xs: 1.5, md: 2 },
                fontSize: '16px',
                color: theme => theme.palette.grey[600],
                fontWeight: 400,
              }}
            >
              Posts
            </Typography>
          </Box>

          {/* Search, sort & filters */}
          <MemoFeedFilterBar spaceId={subSpace.id} filters={searchParams} onChange={handleFiltersChange} />

          {/* Wrapper B — see wrapper A; composer + scheduled section resize
              as they expand/load. */}
          <Box ref={aboveListBRef}>
            {/* Create Post Input */}
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: '15px',
                p: { xs: '12px', md: '15px' },
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <Avatar
                  src={getMediaUrl(profile?.avatar)}
                  alt={profile?.name}
                  sx={{ width: 30, height: 30, borderRadius: '22.5px' }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Collapsed placeholder — shown until expanded (md+) or always
                      on phones, where the composer opens as a bottom sheet. */}
                  {(!composerOpen || isBelowMd) && (
                    <Box
                      onClick={() => setComposerOpen(true)}
                      sx={{
                        bgcolor: '#f4f4f4',
                        border: '1px solid transparent',
                        borderRadius: '12px',
                        px: '17px',
                        py: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease, border-color 0.15s ease',
                        '&:hover': { bgcolor: '#efefef', borderColor: 'grey.300' },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Figtree, sans-serif',
                          fontWeight: 400,
                          fontSize: 14,
                          lineHeight: '19px',
                          color: '#b2b2b2',
                        }}
                      >
                        Start a Post..
                      </Typography>
                      <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <AttachFileOutlinedIcon sx={{ fontSize: 20, color: '#333' }} />
                      </Box>
                    </Box>
                  )}
                  {/* Expanded form (inline md+, bottom sheet on phones) — the same
                      composer used to edit a post. */}
                  <PostComposer
                    spaceId={subSpace.id}
                    containerSpaceId={containerSpaceId}
                    canManageSpace={canManageSpace}
                    open={composerOpen}
                    onOpenChange={setComposerOpen}
                    sheetTitle="Create post"
                    sheetSubtitle="Share something with the community"
                  />
                </Box>
              </Box>
            </Box>

            {/* Admin-only: manage posts scheduled to publish later */}
            <ScheduledPostsSection subSpaceId={subSpace.id} parentSpaceId={parentSpace.id} enabled={canManageSpace} />
          </Box>

          {/* Posts List — virtualized; cards render only around the viewport
              and are measured live (attachments, polls and expanding comments
              all change card heights). */}
          {isLoading ? (
            <FeedPostsSkeleton />
          ) : posts.length === 0 ? (
            <Alert severity="info">
              {hasActiveFeedFilters(searchParams)
                ? 'No posts found — try different tags'
                : 'No posts yet. Be the first to start a discussion!'}
            </Alert>
          ) : (
            <>
              <Box ref={listRef} sx={{ position: 'relative', height: `${virtualizer.getTotalSize()}px` }}>
                {/* Plain map (not million's <For>): the tanstack virtualizer needs
                    the `measureElement` callback ref on each item to measure real
                    heights. million's <For> renders block children that don't
                    forward that ref, so cards collapse to the 420px estimate and
                    leave a large gap under each post. */}
                {virtualizer.getVirtualItems().map(virtualItem => {
                  const post = posts[virtualItem.index];
                  return (
                    <Box
                      key={post.id}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        // item.start includes scrollMargin; positions here are
                        // relative to the list container, so subtract it back.
                        transform: `translateY(${virtualItem.start - listOffset}px)`,
                        // Replaces the old Stack spacing — padding is part of
                        // the measured size, so the gap stays virtual-safe.
                        pb: 2,
                      }}
                    >
                      <PostCard
                        post={post}
                        spaceId={containerSpaceId}
                        selectedTagValues={searchParams.tags}
                        onTagClick={handleTrendingTagClick}
                      />
                    </Box>
                  );
                })}
              </Box>

              {/* Load more sentinel — real DOM below the virtual container, so
                  the existing IntersectionObserver keeps driving fetchNextPage */}
              <Box ref={loadMoreRef} sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                {isFetchingNextPage && <CircularProgress size={24} />}
                {!hasNextPage && posts.length > 0 && (
                  <Typography
                    sx={{
                      fontFamily: 'Figtree, sans-serif',
                      fontSize: 14,
                      color: '#808080',
                    }}
                  >
                    No more posts
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Right sidebar — Who's Online + community widgets. Desktop only; on
            mobile these are condensed into the BannerStatsBar (on the banner)
            and its details drawer — a full sidebar has no room, and stacking
            the cards buries them at the foot of the feed. */}
        {!isBelowMd && (
          <Box
            sx={{
              width: 265,
              flexShrink: 0,
              height: 'calc(100vh - 130px)',
              overflowY: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <MemoWhosOnline height={420} />
            <CommunityQuickFilters
              saved={searchParams.saved}
              mine={searchParams.mine}
              onToggleSaved={() => handleFiltersChange({ saved: searchParams.saved ? undefined : true })}
              onToggleMine={() => handleFiltersChange({ mine: searchParams.mine ? undefined : true })}
            />
            {showCommunityWidgets && (
              <>
                <MemoTrendingTagsWidget
                  spaceId={subSpace.id}
                  selectedTagValues={searchParams.tags}
                  onTagClick={handleTrendingTagClick}
                />
                <MemoCommunityStatsWidget spaceId={subSpace.id} />
                <MemoTopContributorsWidget spaceId={subSpace.id} />
              </>
            )}
          </Box>
        )}
      </Box>
      {showTermsDialog && termsAcceptance?.terms && (
        <FeedTermsAcceptanceDialog spaceId={subSpace.id} terms={termsAcceptance.terms} />
      )}
    </>
  );
};
